import { useTranslations } from 'next-intl';
import type { SetStateAction } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, FormTabType, ImageUploadFile, UploadImageType } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { useGlobalModal } from './use-global-modal';

interface UseImagePromptsOptions {
  formType: Exclude<FormTabType, 'scene' | 'character2video'>;
  maxCount?: number;
  onImagePromptsChange?: () => void;
  allowTempMode?: boolean;
}

export function useImagePrompts({
  formType,
  maxCount = 10,
  onImagePromptsChange,
  allowTempMode = false,
}: UseImagePromptsOptions) {
  const t = useTranslations();
  const store = useStoreApi();
  const { imagePrompts, updateFormParams, setFormParams, userInfo } = useStore((state) => ({
    imagePrompts: state[`${formType}FormParams`].imagePrompts,
    updateFormParams: state.updateFormParams,
    setFormParams: state.setFormParams,
    userInfo: state.userInfo,
  }));
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const pathname = usePathname();

  const setImagePrompts = (updater: SetStateAction<FormImagePrompt[]>) => {
    if (Array.isArray(updater)) {
      updateFormParams(formType, 'imagePrompts', updater);
    } else {
      const prevState = store.getState()[`${formType}FormParams`].imagePrompts || [];
      updateFormParams(formType, 'imagePrompts', updater(prevState as FormImagePrompt[]));
    }
  };

  const getValidImageCount = () =>
    store.getState()[`${formType}FormParams`].imagePrompts.filter((p: FormImagePrompt | null) => p !== null).length;

  const handleUploadImage = (uploadFile: ImageUploadFile) => {
    if (uploadFile.status === 'uploading') {
      if (!userInfo && !allowTempMode) {
        openLoginDialog();
        reportEvent('login_pop_exposure', {
          refer_element: 'upload-image',
          refer_page: pathname,
        });
        return;
      }

      setImagePrompts((prev) => {
        const currentPrompts = (prev ?? []).filter((p) => p !== null);
        if (currentPrompts.some((prompt) => prompt.id === uploadFile.id)) {
          return currentPrompts.map((prompt) =>
            prompt.id === uploadFile.id
              ? {
                  ...prompt,
                  prompt: uploadFile.file ?? prompt.prompt,
                }
              : prompt,
          );
        }
        if (currentPrompts.length >= maxCount) return currentPrompts;

        return [
          ...currentPrompts,
          {
            id: uploadFile.id,
            status: 'uploading',
            prompt: uploadFile.file,
          },
        ];
      });
      return;
    }
    onImagePromptsChange?.();

    if (uploadFile.status === 'error') {
      setImagePrompts((prev) => (prev ?? []).filter((p) => p?.id !== uploadFile.id));
    }

    if (uploadFile.status === 'done' && uploadFile.file) {
      setImagePrompts((prev) =>
        prev.map((prompt) => {
          if (prompt?.id === uploadFile.id) {
            return {
              id: uploadFile.id,
              prompt: uploadFile.file,
              status: 'done' as const,
            };
          }
          return prompt;
        }),
      );
    }
  };

  const handleRemoveImage = (idx: number) => {
    const deletedImagePrompt = imagePrompts[idx];

    const currentFormParams = store.getState()[`${formType}FormParams`];
    const inspirationImageUrl = currentFormParams.inspirationImageUrl;
    if (deletedImagePrompt?.prompt?.sourceImage === inspirationImageUrl) {
      setFormParams(formType, {
        inspirationId: undefined,
        inspirationFrom: undefined,
        inspirationImageUrl: undefined,
      });
    }

    setImagePrompts((prev) => (prev ?? []).filter((_, index) => index !== idx));
    onImagePromptsChange?.();
  };

  const handleImageChange = (idx: number, newImage: ImageUploadFile): UploadImageType | null => {
    const targetPrompt = imagePrompts[idx];

    if (newImage.status === 'removed') {
      handleRemoveImage(idx);
      return null;
    }

    if (!targetPrompt) return null;

    if (newImage.status === 'uploading') {
      return null;
    }

    if (newImage.status === 'done' && newImage.file) {
      setImagePrompts((prev) =>
        prev.map((prompt, index) => {
          if (index === idx && prompt) {
            return {
              id: prompt.id,
              status: 'done' as const,
              prompt: newImage.file,
            };
          }
          return prompt;
        }),
      );
      onImagePromptsChange?.();
      return newImage.file;
    }

    if (newImage.status === 'error') {
      toast({
        type: 'error',
        message: t('create.createForm.upload.error.invalidImage'),
      });
    }

    return null;
  };

  const handleSwapImages = (sourceIdx: number, targetIdx: number) => {
    if (
      sourceIdx < 0 ||
      sourceIdx >= imagePrompts.length ||
      targetIdx < 0 ||
      targetIdx >= imagePrompts.length ||
      sourceIdx === targetIdx
    ) {
      return;
    }

    setImagePrompts((prev) => {
      const newArray = [...prev];
      [newArray[sourceIdx], newArray[targetIdx]] = [newArray[targetIdx], newArray[sourceIdx]];
      return newArray;
    });
    onImagePromptsChange?.();
  };

  const clearImages = () => {
    setImagePrompts([]);
    onImagePromptsChange?.();
  };

  const setImages = (updater: SetStateAction<FormImagePrompt[]>) => {
    setImagePrompts(updater);
    onImagePromptsChange?.();
  };

  return {
    imagePrompts,
    validImageCount: getValidImageCount(),
    handleUploadImage,
    handleRemoveImage,
    handleImageChange,
    handleSwapImages,
    clearImages,
    setImages,
  };
}
